<!--
    UserMenu component is the menu used in the navbar to display all accessible options
 -->
<script setup lang="ts">
/* UTILS */
/* AF LIB */
import { ALAvatar, ALChip } from '@af-lib/vue-ui';
import { computed, ref } from 'vue';

/* API */
import Reporter from '@/api/clients/reporter/reporter';
import { useOverlayStore } from '@/components/drawers/overlayStore';
import ProfileCard from '@/components/profileCard/ProfileCard.vue';
/* COMPONENTS */
import Text from '@/components/text/Text.vue';
/* ENUMS */
import { EXTERNAL_REDIRECTION_MODAL_NAME } from '@/enums/overlaysName';
/* HELPERS */
import { checkExternalRedirect, getMenu } from '@/helpers/navigation';
import { getSpacesRole } from '@/helpers/spaces';
import { getAvatarPath } from '@/helpers/users';
import { useAuthStore } from '@/stores/auth';
/* STORES */
import { useGlobalStore } from '@/stores/global';
/* TYPES */
import type { NavItemType } from '@/types/navigation/navigation.types';

import SpacesMenu from './SpacesMenu.vue';

const user = useAuthStore().getFullUser;
const overlayStore = useOverlayStore();
const userMenu = ref(getMenu('userMenu'));
const profileCardElement = ref();

const hasSpaces = computed(() => getSpacesRole() === 'validSpaces' || getSpacesRole() === 'invalidSpaces');

function isItemNew(item: NavItemType): boolean {
  return typeof item.isNew === 'function' ? item.isNew() : !!item.isNew;
}

function redirectToProfile() {
  profileCardElement.value?.redirectToProfile();
}

function onClickUserMenu(item: NavItemType) {
  if (item.externalRedirection) {
    if (item.externalRedirection?.modalRedirection) {
      overlayStore.toggle(EXTERNAL_REDIRECTION_MODAL_NAME, {
        externalLink: checkExternalRedirect(item)
      });
    } else {
      checkExternalRedirect(item);
    }
  }

  // Send MixPanel event if in nav item
  if (item.reporter) {
    Reporter().reportData(item.reporter.name, item.reporter.params);
  }
}
</script>

<template>
  <div>
    <v-btn class="text-surface" icon>
      <div role="button" :aria-label="$t('accessibility.menu.general')">
        <ALAvatar
          :firstName="user.first_name || ''"
          :lastName="user.last_name || ''"
          :avatarUrl="getAvatarPath(user.branding) || ''"
          :gradients="useGlobalStore().getPersonalityGradients(user.disc_id)"
          :isAPlayer="(user.aplayer_score ?? 0) >= 70"
          :variant="'square'"
          :size="'m'"
          :isGrayscale="false"
        />
      </div>

      <v-menu location="bottom center" activator="parent" offset="11" class="min-w-73.75">
        <v-list class="elevation-3 pb-0 min-w-73.75 pt-0" role="list" :aria-label="$t('accessibility.menu.general')">
          <!-- list header -->
          <v-list-item class="p-0 cursor-pointer" role="listitem" @click="redirectToProfile">
            <ProfileCard ref="profileCardElement" isButton :user="user" class="p-4 w-full" avatarSize="l" noEmail />
          </v-list-item>
          <SpacesMenu v-if="hasSpaces" />
          <v-divider />

          <!-- list content -->
          <!-- :value seems to be mandatory ... Without it, no hover -->
          <v-list-item
            v-for="(item, i) in userMenu"
            :key="i"
            role="listitem"
            :value="item"
            :to="item.route"
            :class="['options-list py-3 px-4', item.class]"
            @click="onClickUserMenu(item)"
          >
            <div role="link" class="d-flex align-center">
              <v-icon :icon="item.icon" class="ms-1 text-icon-4" />
              <Text type="body3"> {{ $t(item.label) }}</Text>
              <ALChip
                v-if="isItemNew(item)"
                :label="$t('general.new')"
                :isInteractive="false"
                :isClosable="false"
                class="border-transparent gradient-border-PT text-af-grey-800 bg-white ms-3 gradient-border-PT text-sm"
                size="s"
              />
            </div>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-btn>
  </div>
</template>

<style scoped lang="scss">
:deep(.options-list .v-icon) {
  margin-inline-end: 10px;
}
</style>
